import {
  ArrowUp,
  Check,
  Clock,
  Download,
  Heart,
  Image,
  Loader2,
  MapPin,
  MessageCircle,
  Play,
  Plus,
  Video,
  X,
} from "lucide-react";
import { useCallback, useEffect, useId, useState } from "react";
import {
  ApiError,
  del,
  get,
  post,
  put,
  type Asset,
  type AssetComment,
  type Member,
  type Trip,
} from "../../lib/api.ts";
import { downloadOriginals } from "../../lib/download.ts";
import { useOnWifi } from "../../lib/network.ts";
import { Link, tripPath } from "../../lib/router.tsx";
import { Avatar, ICON, reason, tagInk } from "../../ui/index.ts";
import { Highlights } from "./Highlights.tsx";
import { CommentForm, MediaComments } from "./MediaComments.tsx";
import { MediaViewer } from "./MediaViewer.tsx";
import { PlaceMap } from "./PlaceMap.tsx";
import { TagPanel } from "./TagPanel.tsx";
import { VideoReel } from "./VideoReel.tsx";

/* Hallmark · component: card (one recording, inside the pager) · genre: playful
 * pre-emit critique: P5 H5 E5 S5 R5 V4
 * states: default · hover · focus · active · disabled · loading · error · success
 * contrast: pass (tokens) · mobile: pass at 320/375/414/768 · tokens: pass
 */

/**
 * One recording, and the few things that are true about it.
 *
 * Every card is the same size, whatever it holds. That is the whole trick behind a pager that
 * does not lurch: the picture's area is decided before anything has loaded, so a photograph
 * arriving — or a swipe landing on the next one — never re-measures anything. A tall picture
 * fills that area; a wide one sits whole inside it, because losing the ends of a landscape shot
 * to a shape is a worse trade than a little quiet grey.
 *
 * Three things ride on the picture because they belong to it: when it was taken, who took it,
 * and what it is filed under. Under it, one line of what the picture cannot show — hearts,
 * remarks, the name of the place — where each fact is the control for itself. Then the
 * conversation, which scrolls inside the card, and the box to add to it along the foot.
 *
 * `live` is how the pager keeps its promises cheaply: the card you are on and its two
 * neighbours do their reading, and the rest wait at the thumbnail the grid already had. A day
 * of four hundred recordings is four hundred cards and three conversations.
 */

type Likes = { liked: boolean; likeCount: number };

/** `14 Oct, 15:36` — the day and the minute, in the reader's own locale. */
const takenAt = (iso: string) =>
  new Date(iso).toLocaleString([], {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
/** `14 Oct` — a file's arrival is a day, not a minute. */
const arrivedAt = (iso: string) =>
  new Date(iso).toLocaleDateString([], { day: "numeric", month: "short" });

/** How many badges ride on the picture before the rest become a count. */
const BADGES = 4;

/** When a recording was taken, or failing that when it reached the trip. */
export function whenOf(asset: Asset): { kind: "taken" | "arrived" | "none"; text: string } {
  if (asset.captureAt) return { kind: "taken", text: takenAt(asset.captureAt) };
  // A server old enough not to send the arrival is better answered with "no date" than with
  // "Invalid Date" printed across somebody's photograph.
  if (asset.createdAt) return { kind: "arrived", text: `Uploaded ${arrivedAt(asset.createdAt)}` };
  return { kind: "none", text: "No date" };
}

/** What one recording is, in words — the pager's own name for the card it is parked on. */
export function describe(asset: Asset): string {
  const kind = asset.kind === "VIDEO" ? "Video" : "Photo";
  const who = asset.own ? "You" : (asset.contributorName ?? "A traveller");
  return `${kind} by ${who}, ${whenOf(asset).text}`;
}

export function MediaCard({
  trip,
  asset,
  members,
  thumb,
  live,
  active,
  onClose,
  onChanged,
}: {
  trip: Trip;
  asset: Asset;
  members: Member[];
  /** The grid's own thumbnail, granted in one batch for the whole collection. */
  thumb: string | null;
  /** This card is the one being read, or next to it: it may do its own reading. */
  live: boolean;
  /** This is the one being read. Its neighbours are loaded scenery and are not announced. */
  active: boolean;
  onClose: () => void;
  onChanged: () => void;
}) {
  const titleId = useId();
  // The recording as it now stands. The grid handed over a snapshot; tagging changes it, so the
  // card re-reads itself rather than drawing tags that are one edit out of date.
  const [item, setItem] = useState(asset);
  const [src, setSrc] = useState<string | null>(null);
  const [playSrc, setPlaySrc] = useState<string | null>(null);
  const [notYet, setNotYet] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** The download's own lifecycle, which the button reports in place rather than in a toast. */
  const [phase, setPhase] = useState<"idle" | "busy" | "done">("idle");
  const [comments, setComments] = useState<AssetComment[]>([]);
  const [tagging, setTagging] = useState(false);
  /** Somebody asked for this clip by tapping it, where it would not have started on its own. */
  const [asked, setAsked] = useState(false);
  const onWifi = useOnWifi();
  /** Full screen: the whole picture, or the video playing. Null while it is shut. */
  const [viewing, setViewing] = useState<{ at: number | null } | null>(null);
  /** Taller than it is wide, as the file itself turned out to be. */
  const [portrait, setPortrait] = useState(false);
  /** The map under the place name, which nobody has asked for until they do. */
  const [mapOpen, setMapOpen] = useState(false);
  /** Where the clip was left when it was last watched; what a timed comment fills in from. */
  const [mark, setMark] = useState<number | null>(null);
  /** The heart, held locally so a tap lands at once and the server only confirms it. */
  const [likes, setLikes] = useState({ liked: asset.liked, count: asset.likeCount });

  const isVideo = item.kind === "VIDEO";
  const verified = item.components.find((c) => c.availability === "VERIFIED");
  const base = `/v1/trips/${trip.id}/assets/${item.id}`;

  useEffect(() => {
    setItem(asset);
    setLikes({ liked: asset.liked, count: asset.likeCount });
  }, [asset]);

  /** Re-read this one recording, and tell the grid behind to re-read its day. */
  const refresh = useCallback(async () => {
    const fresh = await get<Asset>(base).catch(() => null);
    if (fresh) setItem(fresh);
    onChanged();
  }, [base, onChanged]);

  const loadComments = useCallback(
    () =>
      get<{ items: AssetComment[] }>(`${base}/comments`)
        .then((c) => {
          setComments(c.items);
          setError(null);
        })
        .catch((e: Error) => setError(reason(e))),
    [base],
  );
  useEffect(() => {
    if (live) void loadComments();
  }, [live, loadComments]);

  /** Asks for a read URL of one rendition of this recording. */
  const grant = useCallback(
    async (variant: "VIEW" | "PREVIEW") => {
      const key = verified?.key ?? item.components[0]?.key;
      if (key === undefined) return null;
      return (
        await post<{ url: string }>(`/v1/trips/${trip.id}/media-grants`, {
          variant,
          reference: { kind: "ASSET_COMPONENT", assetId: item.id, componentKey: key },
        })
      ).url;
    },
    [trip.id, item.id, item.components, verified?.key],
  );

  // A photo is drawn at the viewer size the worker renders — never the original, which is often a
  // HEIC a browser cannot draw at all. A video asks for nothing until somebody plays it, and a
  // card nobody has reached asks for nothing at all.
  useEffect(() => {
    if (isVideo || !live) return;
    let living = true;
    setNotYet(false);
    grant("VIEW")
      .then((url) => {
        if (living && url) setSrc(url);
      })
      .catch((e: Error) => {
        if (!living) return;
        if (e instanceof ApiError && e.status === 409) setNotYet(true);
        else setError(reason(e));
      });
    return () => {
      living = false;
    };
  }, [grant, isVideo, live]);

  // The clip in the card, fetched when it is going to be played: on Wi-Fi that is as soon as the
  // card is the one being read, and on a mobile network not until somebody taps it. A video is
  // megabytes, and a library of them started by themselves is a bill nobody agreed to.
  useEffect(() => {
    if (!isVideo || !live || playSrc || !(onWifi || asked)) return;
    let living = true;
    grant("PREVIEW")
      .then((url) => {
        if (living && url) setPlaySrc(url);
      })
      .catch((e: Error) => {
        if (!living) return;
        if (e instanceof ApiError && e.status === 409) setNotYet(true);
        else setError(reason(e));
      });
    return () => {
      living = false;
    };
  }, [isVideo, live, playSrc, onWifi, asked, grant]);

  /**
   * A tap moves the heart now and asks afterwards. A like is small enough that waiting for a
   * round trip before the heart fills reads as a broken button; if the server disagrees the
   * next read puts it back, and nothing was lost either way.
   */
  function toggleLike() {
    const next = !likes.liked;
    setLikes((l) => ({ liked: next, count: Math.max(0, l.count + (next ? 1 : -1)) }));
    // Whatever went wrong before was about something else. One card has one place to report a
    // failure, so a stale line left sitting over a button that has just worked is a lie about
    // the action the traveller is actually looking at.
    setError(null);
    (next ? put<Likes>(`${base}/like`, {}) : del<Likes>(`${base}/like`))
      .then((r) => setLikes({ liked: r.liked, count: r.likeCount }))
      .catch((e: Error) => {
        setLikes({ liked: !next, count: item.likeCount });
        setError(reason(e));
      });
  }

  /** Open it full size — the whole picture, or the clip playing, at a moment if one is asked for. */
  function openFull(at: number | null = null) {
    setViewing({ at });
    if (!isVideo || playSrc) return;
    setError(null);
    grant("PREVIEW")
      .then((url) => {
        if (url) setPlaySrc(url);
        else setViewing(null);
      })
      .catch((e: Error) => {
        setViewing(null);
        if (e instanceof ApiError && e.status === 409) setNotYet(true);
        else setError(reason(e));
      });
  }

  async function download() {
    if (!verified) return;
    setPhase("busy");
    setError(null);
    try {
      const r = await downloadOriginals(trip.id, [
        { assetId: item.id, componentKey: verified.key },
      ]);
      if (r.failed[0]) {
        setError(r.failed[0].reason);
        setPhase("idle");
        return;
      }
      // The file lands outside the app, so silence would be ambiguous. Confirmed in place, on the
      // control that was pressed, rather than in a toast that steals the corner of the screen.
      setPhase("done");
      setTimeout(() => setPhase("idle"), 2400);
    } catch (e) {
      setError(reason(e as Error));
      setPhase("idle");
    }
  }

  const who = item.own ? "You" : (item.contributorName ?? "A traveller");
  const whoColor = members.find((m) => m.userId === item.contributorId)?.color ?? null;
  const when = whenOf(item);
  const whenIcon =
    when.kind === "arrived" ? (
      <ArrowUp {...ICON} aria-hidden="true" />
    ) : (
      <Clock {...ICON} aria-hidden="true" />
    );
  // The viewer rendition once it is here, the grid's thumbnail until then: a card is never empty
  // while it waits, and the shape it is drawn in does not depend on which one arrived.
  const still = (isVideo ? null : src) ?? thumb;
  const shown = item.tags.slice(0, BADGES);
  const rest = item.tags.length - shown.length;

  return (
    <article
      className="media-card"
      aria-labelledby={titleId}
      // A pager holds three loaded cards; only one of them is being read. The others are taken
      // out of the accessibility tree so a screen reader is not handed three conversations, and
      // so a swipe between them does not move focus into a card nobody is looking at.
      aria-hidden={active ? undefined : "true"}
      inert={!active}
    >
      {/* The recording is the title here, so the words are for a screen reader. */}
      <h2 id={titleId} className="sr">
        {describe(item)}
      </h2>

      {/* When it was taken, and the two things you do to the file itself. They ride over the
          picture and stay there as the card scrolls, because they belong to the recording rather
          than to the part of it you happen to be looking at. */}
      <div className="card-top">
        <div className="card-top-row">
          <p className="chip media-when">
            {whenIcon}
            {when.text}
          </p>

          <div className="media-frame-over">
            <button
              type="button"
              className="media-over-btn"
              data-phase={phase}
              disabled={!verified || phase === "busy"}
              onClick={() => void download()}
              aria-label={
                phase === "busy"
                  ? "Preparing download"
                  : phase === "done"
                    ? "Saved"
                    : "Download full size"
              }
            >
              {phase === "busy" ? (
                <Loader2 {...ICON} aria-hidden="true" className="spin" />
              ) : phase === "done" ? (
                <Check {...ICON} aria-hidden="true" />
              ) : (
                <Download {...ICON} aria-hidden="true" />
              )}
            </button>
            <button type="button" className="media-over-btn" onClick={onClose} aria-label="Close">
              <X {...ICON} aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>

      <figure
        className="media-frame"
        data-portrait={portrait ? "true" : undefined}
        data-reel={isVideo && playSrc ? "true" : undefined}
      >
        {isVideo && playSrc ? (
          <VideoReel
            src={playSrc}
            poster={thumb}
            label={describe(item)}
            autoPlay={onWifi}
            active={active}
            onFull={() => openFull()}
          />
        ) : (
          <button
            type="button"
            className="media-frame-face"
            // A video is tapped to play it where it sits; only a photograph opens full size on a
            // tap, because a photograph has nothing else a tap could mean.
            onClick={() => (isVideo ? setAsked(true) : openFull())}
            aria-label={isVideo ? "Play this video" : "Open this photo full size"}
          >
            {still ? (
              <img
                src={still}
                alt=""
                onLoad={(e) =>
                  setPortrait(e.currentTarget.naturalHeight > e.currentTarget.naturalWidth)
                }
              />
            ) : (
              <span className="media-frame-blank">
                {notYet ? (
                  <span className="muted small">Still being prepared.</span>
                ) : isVideo ? (
                  <Video size={24} strokeWidth={1.5} aria-hidden="true" />
                ) : (
                  <Image size={24} strokeWidth={1.5} aria-hidden="true" />
                )}
              </span>
            )}
            {isVideo && (
              <span className="media-frame-play" aria-hidden="true">
                <Play size={18} strokeWidth={2} fill="currentColor" />
              </span>
            )}
          </button>
        )}

        {/* Whose it is, and what it is filed under: one row along the foot of the picture,
            because they are the same kind of fact and neither is worth a label. */}
        <figcaption className="media-frame-foot">
          <span className="chip media-by">
            <Avatar name={who} color={whoColor} size="sm" />
            <span className="media-by-name">{who}</span>
          </span>
          {shown.map((t) => (
            <Link
              key={t.id}
              to={tripPath(trip.id, "tags", t.id)}
              className="pill tag-link"
              data-colored="true"
              style={tagInk(t.color)}
            >
              {t.name}
            </Link>
          ))}
          {rest > 0 && (
            <button
              type="button"
              className="pill tag-more"
              onClick={() => setTagging(true)}
              aria-label={`${rest} more tag${rest === 1 ? "" : "s"}`}
            >
              +{rest}
            </button>
          )}
          {/* The way to change them is a badge of the same size, drawn rather than filled: it
              belongs to this row, and an outline says it is the empty one you press rather
              than another folder the recording is already in. */}
          <button
            type="button"
            className="tag-add"
            onClick={() => setTagging(true)}
            aria-label="Add or remove tags"
          >
            <Plus {...ICON} aria-hidden="true" />
            Tag
          </button>
        </figcaption>
      </figure>

      {/* The icon cannot say "Preparing…" out loud, so the state is announced instead. */}
      <p className="sr" role="status">
        {phase === "busy" ? "Preparing the download" : phase === "done" ? "Saved" : ""}
      </p>

      <div className="media-card-body">
        {/* One line, and every fact on it is the control for itself. */}
        <div className="media-meta">
          <button
            type="button"
            className="meta-item"
            data-on={likes.liked}
            aria-pressed={likes.liked}
            onClick={toggleLike}
          >
            <Heart {...ICON} aria-hidden="true" fill={likes.liked ? "currentColor" : "none"} />
            <span aria-hidden="true">{likes.count}</span>
            <span className="sr">
              {likes.liked ? "Liked" : "Like"}
              {likes.count > 0 ? `, ${likes.count} in all` : ""}
            </span>
          </button>
          <button
            type="button"
            className="meta-item"
            onClick={() => document.getElementById(`say-${item.id}`)?.focus()}
          >
            <MessageCircle {...ICON} aria-hidden="true" />
            <span aria-hidden="true">{comments.length}</span>
            <span className="sr">
              {comments.length === 1 ? "1 remark" : `${comments.length} remarks`}, say something
            </span>
          </button>
          {item.location && (
            <button
              type="button"
              className="meta-item place"
              aria-expanded={mapOpen}
              onClick={() => setMapOpen((m) => !m)}
            >
              <MapPin {...ICON} aria-hidden="true" />
              <span className="meta-place">{item.placeName ?? "Somewhere on the trip"}</span>
            </button>
          )}
        </div>

        {mapOpen && item.location && (
          <PlaceMap
            lat={item.location.lat}
            lon={item.location.lon}
            name={item.placeName}
            estimated={item.location.source !== "METADATA"}
          />
        )}

        {!verified && (
          <p className="muted small" style={{ margin: 0 }}>
            Available once the upload is checked. The original is never changed.
          </p>
        )}
        {notYet && (
          <p className="muted small" style={{ margin: 0 }}>
            The preview is still being prepared. Try again in a minute.
          </p>
        )}
        {error && (
          <p role="alert" className="error">
            {error}
          </p>
        )}

        {isVideo && live && (
          <Highlights
            tripId={trip.id}
            assetId={item.id}
            durationMs={item.durationMs}
            members={members}
            comments={comments}
            mark={mark}
            onSeek={(ms) => openFull(ms)}
            onChanged={onChanged}
          />
        )}

        <MediaComments comments={comments} members={members} />
      </div>

      <CommentForm
        id={`say-${item.id}`}
        isVideo={isVideo}
        mark={mark}
        onAdd={async (c) => {
          await post(`${base}/comments`, c);
          await loadComments();
        }}
      />

      {viewing && (isVideo ? playSrc : src) && (
        <MediaViewer
          assetId={item.id}
          src={(isVideo ? playSrc : src)!}
          isVideo={isVideo}
          startAtMs={viewing.at}
          label={describe(item)}
          onClose={(at) => {
            setViewing(null);
            if (at !== null) setMark(at);
          }}
        />
      )}

      {tagging && (
        <TagPanel
          trip={trip}
          assets={[item]}
          onDone={() => {
            setTagging(false);
            void refresh();
          }}
          onClose={() => {
            setTagging(false);
            void refresh();
          }}
        />
      )}
    </article>
  );
}
